import { StyledRange } from "@silevis/reactgrid";
import dayjs from "dayjs";
import { ApplicationContextType } from "../../../../interfaces/interfaces";
import { title } from "process";
import { debtScheduleHeaders } from "../DebtSchedule/debtScheduleHelper";

// don't change the column names, as they are used for key based index from the data
// so if we need to change the column names, we need to change the data as well, therefore change the enum ColumnTitle which is below this enum definition
export enum debtorScheduleColumns {
  receivableFrom = "receivableFrom",
  amountDue = "amountDue",
  dueDate = "dueDate",
  isReceived = "isReceived",
}

export enum debtorScheduleColumnTitle {
  receivableFrom = "Receivable From",
  amountDue = "Amount Due (₹)",
  dueDate = "Due Date",
}

export const debtorScheduleHeaders = [
  debtorScheduleColumns.receivableFrom,
  debtorScheduleColumns.amountDue,
  debtorScheduleColumns.dueDate,
];

export const getDebtorScheduleHeaders = (formType: ApplicationContextType) => {
  if (formType === ApplicationContextType.monthlyReport) {
    return debtorScheduleHeaders.concat(debtorScheduleColumns.isReceived);
  } else return debtorScheduleHeaders;
};

export const debtorScheduleHeadersObj = {
  [debtorScheduleColumns.receivableFrom]: {
    title: debtorScheduleColumnTitle.receivableFrom,
    styles: {
      justifyContent: "flex-start",
    },
    type: "string",
    isTotal: false,
  },
  [debtorScheduleColumns.amountDue]: {
    title: debtorScheduleColumnTitle.amountDue,
    styles: {
      justifyContent: "flex-end",
    },
    type: "number",
    isTotal: true,
  },
  [debtorScheduleColumns.dueDate]: {
    title: debtorScheduleColumnTitle.dueDate,
    styles: {
      justifyContent: "center",
    },
    type: "date",
    isTotal: false,
  },
  [debtorScheduleColumns.isReceived]: {
    title: "Payment Received?",
    styles: {
      justifyContent: "center",
    },
    type: "checkbox",
    isTotal: false,
  },
};

export const getInitialDebtorSchedule = (
  formType: ApplicationContextType
): {
  headers: any[];
  rowData: any[];
  footer: any;
} => {
  const rowData = [];
  const initialRow = {};
  const debtorScheduleHeaders = getDebtorScheduleHeaders(formType);
  debtorScheduleHeaders.forEach((header) => {
    initialRow[header] = "";
  });
  rowData.push(initialRow);

  const initalFooter = {};
  debtorScheduleHeaders.forEach((header, index) => {
    initalFooter[header] = index === 0 ? "Total" : "-";
  });

  const headers = [];
  debtorScheduleHeaders.forEach((header) => {
    headers.push({
      key: header,
      value: debtorScheduleHeadersObj[header].title,
    });
  });
  // const rowData = [Array(debtorScheduleHeaders.length).fill("")];
  // const footer = [
  //   "Total",
  //   ...Array(debtorScheduleHeaders.length - 1).fill("-"),
  // ];

  return {
    headers: headers,
    rowData,
    footer: initalFooter,
  };
};

export const onAddInitialDebtorScheduleRow = (
  formType: ApplicationContextType
) => {
  const initialRow = {};
  const debtorScheduleHeaders = getDebtorScheduleHeaders(formType);
  debtorScheduleHeaders.forEach((header) => {
    initialRow[header] = "";
  });
  return initialRow;
};

export const getDebtorScheduleGridInvalidRange = (
  gridData: any,
  formType: ApplicationContextType
) => {
  let missingDataErrors: StyledRange[] = [];
  let dateErrors: StyledRange[] = [];

  const debtorScheduleHeaders = getDebtorScheduleHeaders(formType);

  gridData.rowData.forEach((row, rowIndex) => {
    // **Check if the row has any data**
    // if () hasData = true;
    const hasData =
      gridData.rowData?.length == 1
        ? true
        : debtScheduleHeaders.some(
            (headerKey) => row[headerKey]?.toString().trim() !== ""
          );
    if (!hasData) return;
    debtorScheduleHeaders.forEach((headerKey, colIndex) => {
      // const columnKey = gridData.headers[colIndex];
      const cell = row[headerKey];
      const columnType = debtorScheduleHeadersObj[headerKey]?.type;

      // **Step 1: Check for Empty Cells in Non-Empty Rows**
      if (
        hasData &&
        (cell?.toString().trim() === "" || !cell) &&
        headerKey != debtorScheduleColumns.isReceived
      ) {
        missingDataErrors.push({
          range: {
            start: { rowIndex: rowIndex + 1, columnIndex: colIndex },
            end: { rowIndex: rowIndex + 2, columnIndex: colIndex + 1 },
          },
          styles: { background: "#FDF1F1" }, // Light red for missing data
        });
      }

      // **Step 2: Check for Date Validations (End Date before Start Date)**
      if (!missingDataErrors.length && columnType === "date") {
        const dueDateIndex = gridData.headers.indexOf(
          debtorScheduleColumns.dueDate
        );

        if (colIndex === dueDateIndex) {
          const dueDate = dayjs(row[dueDateIndex], "DD/MM/YYYY");
          console.log("dueDate", dueDate);

          if (!dueDate.isValid()) {
            dateErrors.push({
              range: {
                start: { rowIndex: rowIndex + 1, columnIndex: colIndex },
                end: { rowIndex: rowIndex + 2, columnIndex: colIndex + 1 },
              },
              styles: { background: "#FDF1F1" }, // Yellow for invalid dates
            });
          }
        }
      }
    });
  });

  return { missingDataErrors, dateErrors };
};

export const findFirstDebtorScheduleGridError = (
  gridData: any,
  formType: ApplicationContextType,
  checkEmpty = true
) => {
  if (!gridData) {
    if (checkEmpty) {
      return {
        type: "missingData",
        message: "At least one value is required.",
        range: {
          start: { rowIndex: 1, columnIndex: 0 },
          end: { rowIndex: 2, columnIndex: 1 },
        },
        styles: { background: "#FDF1F1" }, // Light red
      };
    } else {
      return "";
    }
  }
  const totalRows = gridData.rowData.length;
  const debtorScheduleHeadersForm = getDebtorScheduleHeaders(formType);
  for (let rowIndex = 0; rowIndex < totalRows; rowIndex++) {
    const row = gridData.rowData[rowIndex];

    // **Check if the row has any data**
    const hasData = debtorScheduleHeadersForm.some((headerKey) => {
      return row[headerKey]?.toString().trim() !== "";
    });

    // **Step 1: If only one row and empty, show missing data error**
    if (totalRows === 1 && !hasData && checkEmpty) {
      return {
        type: "missingData",
        message: "At least one value is required.",
        range: {
          start: { rowIndex: 1, columnIndex: 0 },
          end: { rowIndex: 2, columnIndex: gridData.headers.length },
        },
        styles: { background: "#FDF1F1" }, // Light red
      };
    }

    // **Step 2: If multiple rows, check each row with at least one value**
    if (totalRows > 1 && hasData) {
      for (
        let colIndex = 0;
        colIndex < debtorScheduleHeadersForm.length;
        colIndex++
      ) {
        const columnKey = debtorScheduleHeadersForm[colIndex];
        const cell = row[columnKey];
        const columnType = debtorScheduleHeadersObj[columnKey]?.type;

        // console.log("columnKey", columnKey);
        // console.log("row", row);

        // **Step 2.1: If a row has data, check for missing values**
        if (
          (cell?.toString().trim() === "" || !cell) &&
          columnKey != debtorScheduleColumns.isReceived
        ) {
          return {
            type: "missingData",
            message: "Some cells are missing data.",
            range: {
              start: { rowIndex: rowIndex + 1, columnIndex: colIndex },
              end: { rowIndex: rowIndex + 2, columnIndex: colIndex + 1 },
            },
            styles: { background: "#FDF1F1" }, // Light red
          };
        }

        // **Step 3: If no missing values, check for date validation**
        if (columnType === "date") {
          const dueDateIndex = gridData.headers.indexOf(
            debtorScheduleColumns.dueDate
          );

          if (colIndex === dueDateIndex) {
            const dueDate = dayjs(row[dueDateIndex], "DD/MM/YYYY");

            if (!dueDate.isValid()) {
              return {
                type: "invalidDate",
                message: "End date cannot be before start date.",
                range: {
                  start: { rowIndex: rowIndex + 1, columnIndex: colIndex },
                  end: { rowIndex: rowIndex + 2, columnIndex: colIndex + 1 },
                },
                styles: { background: "#FDF1F1" }, // Yellow for invalid dates
              };
            }
          }
        }
      }
    }
  }

  return null; // ✅ No errors found
};
